/**
 * DUEL Waitlist System
 *
 * Three separate waitlists:
 * 1. user  — general early access (free/pro)
 * 2. coach — personal trainers who want to manage clients
 * 3. club  — gyms, schools, sports orgs
 *
 * Saves to public.waitlist table. No auth required.
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('WAITLIST');

export type WaitlistType = 'user' | 'coach' | 'club';

export interface WaitlistConfig {
  title: string;
  subtitle: string;
  placeholder: string;
  ctaLabel: string;
  successMessage: string;
  namePlaceholder: string;
  notesLabel: string;
  notesPlaceholder: string;
}

export const WAITLIST_CONFIGS: Record<WaitlistType, WaitlistConfig> = {
  user: {
    title: 'Get Early Access',
    subtitle: 'Be among the first to compete on DUEL when we open to the public.',
    placeholder: 'your@email.com',
    ctaLabel: 'Join the List',
    successMessage: "You're on the list. We'll reach out when spots open up.",
    namePlaceholder: 'Your name',
    notesLabel: 'Where do you lift?',
    notesPlaceholder: 'Gym name or city',
  },
  coach: {
    title: 'Coach Tier — Early Access',
    subtitle: "For personal trainers. Manage clients, track their progress, and run programming through DUEL. We're building this with early coaches.",
    placeholder: 'your@email.com',
    ctaLabel: 'Join Coach Waitlist',
    successMessage: "You're on the coach waitlist. We'll be in touch soon.",
    namePlaceholder: 'Your name',
    notesLabel: 'Tell us about your coaching',
    notesPlaceholder: 'How many clients? What gym/facility?',
  },
  club: {
    title: 'Club Tier — Early Access',
    subtitle: "For gyms, schools, CrossFit boxes, and sports organizations. We're onboarding early partners to get this right.",
    placeholder: 'your@email.com',
    ctaLabel: 'Join Club Waitlist',
    successMessage: "You're on the club waitlist. We'll reach out to discuss your organization.",
    namePlaceholder: 'Your name or org name',
    notesLabel: 'Tell us about your organization',
    notesPlaceholder: 'Type of org, member count, location',
  },
};

export async function joinWaitlist(params: {
  type: WaitlistType;
  email: string;
  name?: string;
  notes?: string;
}): Promise<{ success: boolean; error?: string }> {
  const { type, email, name, notes } = params;

  if (!email || !email.includes('@')) {
    return { success: false, error: 'Please enter a valid email address.' };
  }

  try {
    const { error } = await supabase.from('waitlist').insert({
      email: email.trim().toLowerCase(),
      list_type: type,
      name: name?.trim() || null,
      notes: notes?.trim() || null,
      source: 'app',
    });

    if (error) {
      // Unique constraint = already on list
      if (error.code === '23505') {
        return { success: true }; // Treat as success — they're already on the list
      }
      log.error('Waitlist insert failed:', error);
      return { success: false, error: 'Something went wrong. Please try again.' };
    }

    return { success: true };
  } catch (err) {
    log.error('Waitlist exception:', err);
    return { success: false, error: 'Unable to connect. Please try again.' };
  }
}
